import type { PublicLetterhead } from './types';

/**
 * Estimates / quotes (tracker item 39): the shapes of /api/estimates,
 * /api/portal/estimates and /api/public/estimates/:token. Money is a decimal
 * string with 2 places; dates are YYYY-MM-DD; timestamps ISO strings.
 */

export const ESTIMATE_STATUSES = ['DRAFT', 'SENT', 'APPROVED', 'DECLINED', 'EXPIRED', 'CONVERTED', 'CANCELLED'] as const;
export type EstimateStatus = (typeof ESTIMATE_STATUSES)[number];
export type EstimateResponseChannel = 'PORTAL' | 'LINK' | 'OFFICE';
export type EstimateConversionTarget = 'JOB' | 'RECURRING';
export type EstimatePricingModel = 'FIXED' | 'HOURLY' | 'PER_ITEM';
export type EstimatePriceSource = 'CATALOG' | 'CHART' | 'MANUAL';

export interface EstimateLine {
  id: string;
  itemId: string;
  itemName: string;
  description: string;
  quantity: string;
  unitPrice: string;
  pricingModel: EstimatePricingModel;
  priceSource: EstimatePriceSource;
  taxRate: string;
  lineAmount: string;
  lineDiscount: string;
  lineTax: string;
  lineTotal: string;
  /** Service bundles (tracker item 85): the group a line belongs to; all null for a plain line */
  bundleId: string | null;
  /** The bundle's name (a snapshot, kept when the bundle is deleted since) */
  bundleName: string | null;
  /** Planned visit: days after the bundle's first visit */
  bundleOffsetDays: number | null;
  /** The start date chosen for the bundle (YYYY-MM-DD); null = asked at conversion */
  bundleStartDate: string | null;
}

/** One bundle on an estimate: its lines grouped under the bundle header. */
export interface EstimateBundleGroup {
  /** null when the bundle was deleted since */
  bundleId: string | null;
  bundleName: string;
  startDate: string | null;
  lineCount: number;
  /** Ids of the group's lines, in visit order */
  lineIds: string[];
  subtotal: string;
  totalDiscount: string;
  totalTax: string;
  total: string;
}

export interface EstimateDiscount {
  discountId: string | null;
  name: string;
  type: 'PERCENTAGE' | 'FIXED_AMOUNT';
  value: string;
  amount: string;
}

export interface Estimate {
  id: string;
  estimateNumber: string;
  /** A SENT estimate past its expiry date reads EXPIRED */
  status: EstimateStatus;
  customer: { id: string; name: string; email: string | null };
  property: { id: string; address: string; city: string; state: string; zip: string };
  title: string | null;
  customerMessage: string | null;
  internalNotes: string | null;
  expiryDate: string;
  lineItems: EstimateLine[];
  /** Bundle groups of the lines (empty without bundles) */
  bundles: EstimateBundleGroup[];
  discounts: EstimateDiscount[];
  subtotal: string;
  totalDiscount: string;
  totalTax: string;
  total: string;
  sentAt: string | null;
  lastSentAt: string | null;
  sendCount: number;
  respondedAt: string | null;
  respondedVia: EstimateResponseChannel | null;
  responderName: string | null;
  responseComment: string | null;
  convertedAt: string | null;
  convertedJobId: string | null;
  convertedScheduleId: string | null;
  /** Enrollments created from the bundle lines at conversion */
  convertedEnrollmentIds: string[];
  cancelledAt: string | null;
  cancelReason: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export type EstimateSummary = Omit<Estimate, 'lineItems' | 'discounts' | 'customerMessage' | 'internalNotes'> & { lineCount: number };

export interface EstimatePage<T> {
  data: T[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface EstimateListQuery {
  status?: EstimateStatus | '';
  customerId?: string;
  propertyId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface EstimateLineInput {
  itemId: string;
  quantity: string | number;
  /** A manual price (0 allowed); omitted / '' = the item's chart or catalog price */
  unitPrice?: string | number | null;
  pricingModel?: EstimatePricingModel;
  description?: string | null;
  /**
   * Bundle lines (tracker item 85): PATCH lineItems replaces every line, so a bundle line is sent back with its
   * group. A line of a bundle deleted since has no bundleId: send bundleName instead.
   */
  bundleId?: string | null;
  bundleName?: string | null;
  bundleOffsetDays?: number | null;
  bundleStartDate?: string | null;
}

export interface EstimateInput {
  propertyId: string;
  title?: string | null;
  customerMessage?: string | null;
  internalNotes?: string | null;
  expiryDate?: string | null;
  lineItems?: EstimateLineInput[];
  /** Omitted / null = every applicable discount; a list = exactly those */
  discountIds?: string[] | null;
}

export type EstimateUpdateInput = Omit<Partial<EstimateInput>, 'propertyId'>;

export interface EstimatePreview {
  lineItems: Omit<EstimateLine, 'id'>[];
  discounts: EstimateDiscount[];
  applicableDiscounts: { discountId: string; name: string; type: 'PERCENTAGE' | 'FIXED_AMOUNT'; value: string; selected: boolean }[];
  subtotal: string;
  totalDiscount: string;
  totalTax: string;
  total: string;
}

export interface EstimateSendInput {
  expiryDate?: string | null;
  idempotencyKey?: string;
}

export interface EstimateSendResult {
  estimate: Estimate;
  message: { id: string; status: string; skipReason: string | null; skipReasonText: string | null; toAddress: string | null };
  /** false when the same idempotency key was already used (nothing changed) */
  created: boolean;
}

export interface EstimateResponseInput {
  name?: string | null;
  comment?: string | null;
}

export interface EstimateConvertInput {
  target: EstimateConversionTarget;
  /** The date the first remaining visit of every bundle falls on (YYYY-MM-DD) */
  startDate?: string;
  job?: { scheduledDate?: string | null; assignedTo?: string | null };
  recurring?: {
    frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
    interval?: number;
    frequencyDetails?: { daysOfWeek?: number[]; dayOfMonth?: number };
    startDate: string;
    endDate?: string | null;
    assignedTo: string;
    notes?: string | null;
  };
}

export interface EstimateConversionResult {
  estimate: Estimate;
  /** null when every line was a bundle line */
  jobId: string | null;
  scheduleId: string | null;
  /** One enrollment per bundle on the estimate */
  enrollmentIds: string[];
}

/** POST /estimates/:id/bundles: the estimate plus the bundle components that got no line. */
export type EstimateBundleAddResult = Estimate & {
  skipped: { componentId: string; sortOrder: number; itemId: string; itemName: string; reason: string }[];
};

/** 409 BUNDLE_START_DATE_REQUIRED details */
export interface EstimateBundleStartRequired {
  bundles: { bundleId: string | null; bundleName: string }[];
}

/** What the customer sees (portal and public link): no internal notes. */
export interface CustomerEstimateView {
  estimateNumber: string;
  status: EstimateStatus;
  title: string | null;
  customerMessage: string | null;
  customerName: string;
  propertyAddress: string;
  issueDate: string | null;
  expiryDate: string;
  lines: {
    description: string;
    quantity: string;
    unitPrice: string;
    amount: string;
    discount: string;
    tax: string;
    /** Service bundles: the program the line is a visit of, and when (days after the first visit) */
    bundleName?: string | null;
    bundleOffsetDays?: number | null;
  }[];
  discounts: { name: string; amount: string }[];
  subtotal: string;
  totalDiscount: string;
  totalTax: string;
  total: string;
  respondedAt: string | null;
  responseComment: string | null;
  canRespond: boolean;
}

export interface PortalEstimate extends CustomerEstimateView {
  id: string;
  propertyId: string;
}

export interface PublicEstimateView extends CustomerEstimateView {
  company: PublicLetterhead;
}

export type EstimateDecision = 'APPROVED' | 'DECLINED';
